import React, { useState } from "react";
import {
    Box,
    Button,
    Typography,
    Card,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Select,
    FormControl,
    Paper,
    Divider,
    IconButton,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { FaCloudUploadAlt, FaTimes, FaFilePdf, FaFileExcel } from "react-icons/fa";
import COLORS from "../../../../../constants/colors";
import baseURL from "../../config";
import { toast } from "react-toastify";

// This component now receives 'campaigns' and 'onAccountabilitySubmitted' as props
const CampaignAccountability = ({ campaigns, onAccountabilitySubmitted }) => {
    const [open, setOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState("");
    const [message, setMessage] = useState("");
    const [file, setFile] = useState(null);

    // Filter campaigns that are 'closed' and haven't had accountability submitted yet
    const campaignsToAccountFor = campaigns.filter(
        (campaign) => campaign.status === "closed" && !campaign.hasAccountability
    );

    // Handle File Drop
    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'application/pdf': ['.pdf'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        },
        onDrop: (acceptedFiles) => {
            const uploadedFile = acceptedFiles[0];
            if (uploadedFile) {
                setFile(uploadedFile);
            }
        },
    });

    // Remove File
    const handleRemoveFile = () => {
        setFile(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedCampaign || !message || !file) {
            toast.error("Please fill all fields before submitting.");
            return;
        }

        const formData = new FormData();
        formData.append("campaignId", selectedCampaign);
        formData.append("message", message);
        formData.append("file", file);

        try {
            const response = await fetch(`${baseURL}/user/accountability`, {
                method: "POST",
                body: formData,
                credentials: "include",
            });

            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = errorData?.message || errorData?.error || "An error occurred during submission.";
                toast.error(`Submission failed: ${errorMessage}`);
                return;
            }

            toast.success("Accountability submitted successfully!");

            // Reset form and close dialog
            setSelectedCampaign("");
            setMessage("");
            setFile(null);
            setOpen(false);

            // Call the prop function to tell the parent to refetch data
            if (onAccountabilitySubmitted) {
                onAccountabilitySubmitted();
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("Something went wrong. Please try again later.");
        }
    };

    return (
        <>
            <Card
                sx={{
                    p: 4,
                    borderRadius: 3,
                    boxShadow: 0,
                    bgcolor: "#fff",
                    border: `1px solid ${COLORS.border}`,
                    textAlign: "left",
                    mx: "auto",
                }}
            >
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, fontSize: "1.1rem" }}>
                    Provide Campaign Accountability
                </Typography>
                <Typography variant="body2" sx={{ color: "#666", mb: 2, fontSize: "0.9rem" }}>
                    Ensure transparency by updating donors on how the funds were used. Provide a message and upload a supporting file. You will be required to account for each campaign raised.
                </Typography>
                <Typography variant="body2" sx={{ color: "#666", mb: 2, fontSize: "0.9rem" }}>
                    You can only provide accountability for closed campaigns. Once your campaign reaches its goal you will be able to see it in the drop down.
                </Typography>
                <Button
                    variant="contained"
                    size="small"
                    sx={{
                        textTransform: "none",
                        backgroundColor: COLORS.primary,
                        boxShadow: 0,
                        borderRadius: "8px",
                        fontWeight: "bold",
                        fontSize: "0.9rem",
                        px: 2,
                        py: 1,
                        "&:hover": { boxShadow: 0 },
                    }}
                    onClick={() => setOpen(true)}
                    disabled={campaignsToAccountFor.length === 0}
                >
                    {campaignsToAccountFor.length === 0 ? "No Campaigns to Account For" : "Submit Accountability"}
                </Button>
            </Card>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="sm"
                sx={{
                    "& .MuiPaper-root": {
                        borderRadius: 3,
                        padding: 0,
                        margin: { xs: "10px", sm: "0 auto" },
                        width: "auto",
                    },
                    padding: 0,
                }}
            >
                <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: "bold" }}>Submit Campaign Accountability</DialogTitle>
                <Divider />

                <DialogContent sx={{ py: 3, px: 3 }}>
                    <Box
                        sx={{
                            p: 2,
                            mb: 2,
                            backgroundColor: "#f0f8ff",
                            borderLeft: `4px solid ${COLORS.primary}`,
                            borderRadius: "8px",
                            boxShadow: 0,
                        }}
                    >
                        <Typography variant="body1" fontWeight="bold">
                            Why is Accountability Important?
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, color: "#555" }}>
                            Ensuring transparency helps build trust with donors. Your report will be sent to donors who provided their email addresses during their donation.
                        </Typography>
                    </Box>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <Select
                            displayEmpty
                            value={selectedCampaign}
                            onChange={(e) => setSelectedCampaign(e.target.value)}
                            sx={{
                                borderRadius: "12px",
                                backgroundColor: "#f9f9f9",
                                fontSize: "0.95rem",
                                "& .MuiSelect-select": { padding: "12px" },
                                "& fieldset": { borderColor: "#ddd" },
                                "&:hover fieldset": { borderColor: COLORS.primary },
                                "&.Mui-focused fieldset": { borderColor: COLORS.primary },
                            }}
                        >
                            <MenuItem disabled value="">
                                Select a campaign to account for
                            </MenuItem>
                            {campaignsToAccountFor.map((campaign) => (
                                <MenuItem key={campaign.id} value={campaign.id}>
                                    {campaign.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        placeholder="Describe how the funds were used..."
                        multiline
                        rows={3}
                        variant="outlined"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        sx={{
                            mb: 2,
                            fontSize: "0.95rem",
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                backgroundColor: "#f9f9f9",
                                padding: "12px",
                                "& fieldset": { borderColor: "#ddd" },
                                "&:hover fieldset": { borderColor: COLORS.primary },
                                "&.Mui-focused fieldset": { borderColor: COLORS.primary },
                            },
                        }}
                        required
                    />

                    <Box
                        {...getRootProps()}
                        sx={{
                            textAlign: "center",
                            backgroundColor: "#f5f5f5",
                            p: 3,
                            borderRadius: "12px",
                            border: "2px dashed #ccc",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#ebebeb" },
                        }}
                    >
                        <input {...getInputProps()} />
                        {file ? (
                            <Paper
                                elevation={1}
                                sx={{
                                    p: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    width: "100%",
                                    borderRadius: "12px",
                                }}
                            >
                                {file.type === "application/pdf" ? (
                                    <FaFilePdf size={40} color="red" />
                                ) : (
                                    <FaFileExcel size={40} color="green" />
                                )}
                                <Typography sx={{ ml: 2 }}>{file.name}</Typography>
                                <IconButton onClick={handleRemoveFile}>
                                    <FaTimes />
                                </IconButton>
                            </Paper>
                        ) : (
                            <>
                                <FaCloudUploadAlt size={40} color={COLORS.primary} />
                                <Typography sx={{ fontSize: "0.9rem", color: "#666", mt: 1 }}>
                                    Drag & Drop or click to upload (PDF, Excel)
                                </Typography>
                            </>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)} sx={{ textTransform: "none", color: COLORS.danger }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} variant="contained" sx={{ textTransform: "none", backgroundColor: COLORS.primary, boxShadow: 0 }}>
                        Submit Accountability
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CampaignAccountability;